import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './Header.css';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVenturesDropdownOpen, setIsVenturesDropdownOpen] = useState(false);
  const [isMobileVenturesDropdownOpen, setIsMobileVenturesDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-container') && !target.closest('.dropdown-menu')) {
        setIsVenturesDropdownOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsMobileVenturesDropdownOpen(false);
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleVenturesDropdown = () => {
    setIsVenturesDropdownOpen(!isVenturesDropdownOpen);
  };

  const toggleMobileVenturesDropdown = () => {
    setIsMobileVenturesDropdownOpen(!isMobileVenturesDropdownOpen);
  };

  const handleVenturesHover = () => {
    setIsVenturesDropdownOpen(true);
  };

  const handleVenturesLeave = () => {
    // Add a small delay to prevent flickering when moving between trigger and dropdown
    setTimeout(() => {
      setIsVenturesDropdownOpen(false);
    }, 150);
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* Left Navigation */}
        <nav className="nav-left">
          <ul className="nav-list">
            <li><Link to="/" onClick={() => setIsMenuOpen(false)}>Home</Link></li>
            <li><Link to="/about" onClick={() => setIsMenuOpen(false)}>Our Story</Link></li>
            <li 
              className="dropdown-container"
              onMouseEnter={handleVenturesHover}
              onMouseLeave={handleVenturesLeave}
            >
              <a 
                href="#ventures" 
                onClick={(e) => {
                  e.preventDefault();
                  toggleVenturesDropdown();
                }}
                className="dropdown-trigger"
              >
                Ventures
                <FontAwesomeIcon 
                  icon={faChevronDown} 
                  className={`chevron-icon ${isVenturesDropdownOpen ? 'rotated' : ''}`}
                />
              </a>
              <ul 
                className={`dropdown-menu ${isVenturesDropdownOpen ? 'show' : ''}`}
                onMouseEnter={handleVenturesHover}
                onMouseLeave={handleVenturesLeave}
              >
                <li><Link to="/real-estate" onClick={() => setIsVenturesDropdownOpen(false)}>Real Estate</Link></li>
                <li><Link to="/hospitality" onClick={() => setIsVenturesDropdownOpen(false)}>Hospitality</Link></li>
                <li><Link to="/commercial" onClick={() => setIsVenturesDropdownOpen(false)}>Commercial</Link></li>
                <li><Link to="/automobile" onClick={() => setIsVenturesDropdownOpen(false)}>Automobile</Link></li>
                <li><Link to="/banking_finance" onClick={() => setIsVenturesDropdownOpen(false)}>Banking & Finance</Link></li>
                <li><Link to="/agriculture" onClick={() => setIsVenturesDropdownOpen(false)}>Agriculture</Link></li>
              </ul>
            </li>
          </ul>
        </nav>

        {/* Center Logo */}
        <div className="logo-container">
          <Link to="/" onClick={() => setIsMenuOpen(false)} className="logo-link">
            <div className="logo">
              <img src="/logo.png" alt="Ansuvara Group" className="logo-image" />
            </div>
          </Link>
        </div>

        {/* Right Navigation */}
        <nav className="nav-right">
          <ul className="nav-list">
            <li><Link to="/media" onClick={() => setIsMenuOpen(false)}>Media</Link></li>
            <li><Link to="/csr" onClick={() => setIsMenuOpen(false)}>CSR</Link></li>
            <li><Link to="/contact-us" onClick={() => setIsMenuOpen(false)}>Connect With Us</Link></li>

          </ul>
        </nav>

        {/* Mobile Menu Toggle */}
        <div className="mobile-menu-toggle" onClick={toggleMenu}>
          <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
        </div>

        {/* Mobile Navigation */}
        <nav className={`nav-mobile ${isMenuOpen ? 'nav-open' : ''}`}>
          <ul className="nav-list">
            <li><Link to="/" onClick={() => setIsMenuOpen(false)}>Home</Link></li>
            <li><Link to="/about" onClick={() => setIsMenuOpen(false)}>Our Story</Link></li>
            <li className="mobile-dropdown-container">
              <a 
                href="#ventures" 
                onClick={(e) => {
                  e.preventDefault();
                  toggleMobileVenturesDropdown();
                }}
                className="mobile-dropdown-trigger"
              >
                Ventures
                <FontAwesomeIcon 
                  icon={faChevronDown} 
                  className={`mobile-chevron-icon ${isMobileVenturesDropdownOpen ? 'rotated' : ''}`}
                />
              </a>
              {isMobileVenturesDropdownOpen && (
                <ul className="mobile-dropdown-menu">
                  <li><Link to="/real-estate" onClick={() => setIsMobileVenturesDropdownOpen(false)}>Real Estate</Link></li>
                  <li><Link to="/hospitality" onClick={() => setIsMobileVenturesDropdownOpen(false)}>Hospitality</Link></li>
                  <li><Link to="/commercial" onClick={() => setIsMobileVenturesDropdownOpen(false)}>Commercial</Link></li>
                  <li><Link to="/automobile" onClick={() => setIsMobileVenturesDropdownOpen(false)}>Automobile</Link></li>
                  <li><Link to="/banking_finance" onClick={() => setIsMobileVenturesDropdownOpen(false)}>Banking & Finance</Link></li>
                  <li><Link to="/agriculture" onClick={() => setIsMobileVenturesDropdownOpen(false)}>Agriculture</Link></li>
                </ul>
              )}
            </li>
            <li><Link to="/media" onClick={() => setIsMenuOpen(false)}>Media</Link></li>
            <li><Link to="/csr" onClick={() => setIsMenuOpen(false)}>CSR</Link></li>
            <li><Link to="/contact-us" onClick={() => setIsMenuOpen(false)}>Connect With Us</Link></li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header; 